/**
 * The printed foreword (dedication) page, laid out exactly as the print PDF
 * draws it, so Preview can show what will print.
 *
 * A port of the layout in the API's `services/forewordPageImage.js`
 * (renderForewordPagePng): same canvas (8.75" × 11.5" at 300 DPI), margins,
 * type sizes, colours, character-count line wrapping and vertical centring.
 * Keep the two in step: a change to the printed page needs the same change
 * here (forewordPage.test.js pins numbers taken from the API's renderer).
 *
 * Print rules mirrored here: the page prints on the inside front cover only
 * when the message has text; the title prints as stored (blank = no title,
 * "Foreword" reads "For you"). Text prints as printableText leaves it.
 */

import { PRINT_EMOJI_FONT, printableText, withoutStrayJoiners } from "./printableText";

export const FOREWORD_PAGE = { width: 2625, height: 3450 };

export const FOREWORD_TITLE_FONT = `'DM Serif Display', ${PRINT_EMOJI_FONT}, serif`;
export const FOREWORD_BODY_FONT = `'DM Sans', ${PRINT_EMOJI_FONT}, sans-serif`;

export const FOREWORD_COLORS = {
  darkCocoa: "#2A1F1A",
  warmSienna: "#8C6D55",
  deepHoney: "#B89B72",
  frameStroke: "#BAA88C",
};

const pt300 = (pt) => Math.round((pt * 300) / 72);

export function forewordPrints(description) {
  return !!forewordTextForPrint(description).trim();
}

export function normalizeForewordTitle(raw) {
  const t = (raw || "").trim();
  if (!t) return "";
  if (/^foreword$/i.test(t)) return "For you";
  return t;
}

export function normalizeForewordText(raw) {
  return withoutStrayJoiners(String(raw || ""))
    .replace(/\r\n?/g, "\n")
    .replace(/\u2029/g, "\n\n")
    .replace(/[\u0085\v\f]/g, "\n")
    .replace(/[\u200B\u200C\u2060\uFEFF]/g, "")
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n");
}

/** The message exactly as it prints: tidied whitespace, nothing the print fonts can't draw. */
export function forewordTextForPrint(raw) {
  return printableText(normalizeForewordText(raw));
}

function wrapText(text, maxCharsPerLine) {
  const lines = [];
  for (const rawLine of text.split("\n")) {
    let currentLine = "";
    for (const word of rawLine.split(" ")) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        lines.push(word);
        currentLine = "";
      }
    }
    if (currentLine) lines.push(currentLine);
  }
  return lines.length ? lines : [""];
}

/**
 * Where everything sits on the printed page, in print pixels.
 * Returns { width, height, frame, title, titleSize, bodySize, dividers, lines }.
 */
export function layoutForewordPage({ introKeyword, description }) {
  const { width: WIDTH, height: HEIGHT } = FOREWORD_PAGE;
  const marginOuter = pt300(32);
  const padH = pt300(56);
  const padV = pt300(64);
  const titleSize = pt300(28);
  const bodySize = pt300(12);
  const lineHeight = bodySize * 1.9;
  const paragraphGap = Math.round(lineHeight * 1.65);
  const divW = pt300(40);
  const titleToDividerGap = pt300(12);
  const gapAfterDivider = pt300(28);
  const gapBeforeBottomDivider = pt300(28);

  const innerTop = marginOuter + padV;
  const innerBottom = HEIGHT - marginOuter - padV;
  const innerH = innerBottom - innerTop;
  const textColumnW = WIDTH - 2 * marginOuter - 2 * padH;
  const charsPerLine = Math.max(12, Math.floor(textColumnW / (bodySize * 0.52)));

  const title = normalizeForewordTitle(printableText(introKeyword));
  const hasTitle = Boolean(title);

  const wrappedParagraphs = forewordTextForPrint(description)
    .split(/\n\n+/)
    .filter((p) => p.trim().length > 0)
    .map((p) => wrapText(p.trim(), charsPerLine));
  const hasBody = wrappedParagraphs.length > 0 && wrappedParagraphs.some((lines) => lines.some((l) => l.trim().length));

  const bodyLineCount = hasBody ? wrappedParagraphs.reduce((sum, lines) => sum + lines.length, 0) : 0;
  const bodyParaBreaks = hasBody ? Math.max(0, wrappedParagraphs.length - 1) : 0;
  const bodyBaselineSpan = hasBody ? (bodyLineCount - 1) * lineHeight + bodyParaBreaks * (paragraphGap - lineHeight) : 0;

  const titleBlockH = Math.round(titleSize * 1.15);
  const divH = 1;
  const titleSectionH = hasTitle ? titleBlockH + titleToDividerGap : 0;
  let middleBlockH = 0;
  if (hasBody) {
    middleBlockH = titleSectionH + divH + gapAfterDivider + bodyBaselineSpan + gapBeforeBottomDivider + divH;
  } else if (hasTitle) {
    middleBlockH = titleBlockH + titleToDividerGap + divH + gapAfterDivider + divH;
  }

  const blockStartY = innerTop + Math.max(0, (innerH - middleBlockH) / 2);
  const titleBaseline = hasTitle ? blockStartY + Math.round(titleSize * 0.88) : 0;
  const div1Y = hasTitle ? titleBaseline + titleToDividerGap : blockStartY;
  const bodyStartY = div1Y + divH + gapAfterDivider;
  const div2Y = hasBody ? bodyStartY + bodyBaselineSpan + gapBeforeBottomDivider : div1Y + divH + gapAfterDivider;

  // Absolute baselines (the API chains dy offsets; the sums are the same).
  const lines = [];
  if (hasBody) {
    let y = bodyStartY;
    wrappedParagraphs.forEach((paraLines, paraIndex) => {
      paraLines.forEach((text, lineIndex) => {
        if (lines.length > 0) y += lineIndex === 0 && paraIndex > 0 ? paragraphGap : lineHeight;
        lines.push({ text, y });
      });
    });
  }

  return {
    width: WIDTH,
    height: HEIGHT,
    frame: { x: marginOuter, y: marginOuter, width: WIDTH - 2 * marginOuter, height: HEIGHT - 2 * marginOuter },
    title: hasTitle ? { text: title, y: titleBaseline } : null,
    titleSize,
    bodySize,
    dividers: [
      { x: (WIDTH - divW) / 2, y: div1Y, width: divW, height: divH },
      { x: (WIDTH - divW) / 2, y: div2Y, width: divW, height: divH },
    ],
    lines,
  };
}

/**
 * True when the printed dedication runs into the bottom fifth of the page, where Preview's
 * "Read up close" button sits; the button then goes just below the page instead
 * (docs/design/HANDOFF.md, "Dedication up close on Preview").
 */
export function forewordReachesBottom({ introKeyword, description }, fraction = 0.8) {
  const page = layoutForewordPage({ introKeyword, description });
  const lowest = Math.max(0, ...page.dividers.map((d) => d.y + d.height), ...page.lines.map((l) => l.y));
  return lowest > page.height * fraction;
}
